import { useMemo } from "react";
import { pageAdapter } from "../lib/adapters/storyblok";
import { storyblokEditable, useStoryblokState } from "@storyblok/react";

export function useStoryblokLivePage({
  sbPreviewAllowed,
  pageAdapterProps,
  initialComponents,
  sbRelationResolveKey
}: {
    sbPreviewAllowed: boolean;
    pageAdapterProps: any;
    initialComponents: any[];
    sbRelationResolveKey?: string;
}) {
  // Always call hooks in the same order
  const baseCacheEntry = pageAdapterProps?.pagesCache?.[0] || {};
  const baseStory = baseCacheEntry?.value || baseCacheEntry;
  const baseCacheKey = baseCacheEntry?.cacheKey || pageAdapterProps?.pagesInfo?.[0]?.cacheKey;
  const sbStory = useStoryblokState(baseStory, {
    resolveRelations: sbRelationResolveKey
  });

  // Gate logic after hooks
  const components = useMemo(() => {
    if (!sbPreviewAllowed) {
      return initialComponents;
    }
    if (!sbStory || !pageAdapterProps) {
      return initialComponents;
    }
    try {
      const pagesCache = pageAdapter({
        ...pageAdapterProps,
        pagesCache: [{
          value: sbStory,
          cacheKey: baseCacheKey
        }]
      })
      const cacheKey = baseCacheKey;
      const liveEntry = cacheKey ? pagesCache?.[cacheKey] : null;
      if (!liveEntry || !Array.isArray(liveEntry.components)) {
        return initialComponents;
      }
      return liveEntry.components.map(component => {
        if (!component?.type) {
          return component;
        }
        const blok = component.props || {};
        let editableProps = {};
        let blokForEditable = blok;
        if (blok.__ref_editable){
          blokForEditable = {
            ...(blok.reference?.content || blok),
            _uid: blok.__ref_uid,
            _editable: blok.__ref_editable
          }
        }
        if (blokForEditable?._editable) {
          editableProps = storyblokEditable(blokForEditable);
        }
        return {
          ...component,
          editableProps
        };
      });
    } catch (_err) {
      return initialComponents;
    }
  }, [
    sbPreviewAllowed,
    (sbStory as any)?.cv,
    baseCacheKey,
    pageAdapterProps,
    initialComponents
  ]);
  return {
    components,
    sbStory: sbPreviewAllowed ? sbStory : null
  };
}

export default useStoryblokLivePage;
